//===----------------------------------------------------------------------===//
//
//  Copyright 2024-2026 Trovares Inc. dba Rocketgraph.  All rights reserved.
//
//===----------------------------------------------------------------------===//

import { useEffect, useRef } from 'react';

import {
  Box, IconButton, List, ListItem, ListItemButton, ListItemIcon,
  ListItemText, Popover, Switch, Tooltip, Typography
} from '@mui/material';
import StopCircleIcon from '@mui/icons-material/StopCircle';

import { runningDescription, type ToolEntry } from './toolEntries';
import { type PinnedTools } from './usePinnedTools';
import { usePanels } from '@/hooks/usePanels';

interface ToolsPopoverProps {
  tools: ToolEntry[];
  pinned: PinnedTools;
  // Null when closed. The element the popover points at -- the control that
  // opened it, which is not in this component: the trigger lives in the
  // identity menu, since which tools a user keeps is a setting of theirs.
  anchor: HTMLElement | null;
  onClose: () => void;
}

/**
 * Everything a user can open, and which of those they keep on the taskbar.
 *
 * Deliberately a popover list rather than a menu. Each row carries two
 * actions -- open this, and pin this -- and a `menuitem` may not contain a
 * focusable descendant, so a switch inside one is invalid ARIA that screen
 * readers handle inconsistently. A list of rows whose name is a button and
 * whose switch is a sibling is the shape that supports both. The cost is
 * that keyboard users tab rather than arrow through; at this size that is
 * unremarkable, and a roving-focus grid is the fix if the list ever grows.
 *
 * Toggling a pin leaves the popover open, because pinning is something a
 * user does several of at once. Opening a tool closes it, because they are
 * done here.
 */
const ToolsPopover = ({
  tools, pinned, anchor, onClose
}: ToolsPopoverProps) => {
  const { isPanelMounted, isPanelOpen, closePanel, getPanelSpec } =
    usePanels();
  // Each row's pin switch, so focus has somewhere to go when the Stop button
  // beside it takes itself off screen.
  const pinInputs = useRef(new Map<string, HTMLInputElement>());
  // The row whose Stop button was just pressed. Read after the commit that
  // unmounted it, which is the earliest the replacement is on screen.
  const pendingFocus = useRef<string | null>(null);
  // Set when this popover is asked to close: the control to hand focus back
  // to, and what held focus at the time. Held because the anchor prop is
  // already null by the time the handoff can be made.
  const pendingRestore = useRef<
    { target: HTMLElement; held: Element | null } | null>(null);

  const isRunning = (tool: ToolEntry) =>
    tool.panelId !== undefined && isPanelMounted(tool.panelId);

  const isOpen = (tool: ToolEntry) =>
    tool.panelId !== undefined && isPanelOpen(tool.panelId);

  // Whether this list may release the tool at all. A spec that declares
  // itself un-closable is holding something -- chat's transcript, a plugin
  // frame's accumulated state -- that no control may discard; such a tool
  // gets no Stop button and unpinning it leaves it running. An unregistered
  // id has nothing to protect, so it defaults to stoppable.
  const isStoppable = (tool: ToolEntry) =>
    tool.panelId !== undefined &&
    (getPanelSpec(tool.panelId)?.isClosable ?? true);

  // Stopping a tool destroys the control that stopped it: the button is drawn
  // only while the tool runs. Left alone, focus lands on the body and the
  // popover's focus trap drops it back on the container, so a keyboard user
  // loses their place in the list for having used it. The pin switch is the
  // survivor in the same row.
  //
  // No dependency array: this has to run after whichever commit removed the
  // button, and the ref guard makes every other render a no-op.
  useEffect(() => {
    const id = pendingFocus.current;
    if (id === null) return;
    pendingFocus.current = null;
    pinInputs.current.get(id)?.focus();
  });

  // Every way out of this popover, so focus lands somewhere a keyboard user
  // can carry on from. MUI restores focus to the element that gave it away,
  // which here is a row in the identity menu -- a row that unmounts with the
  // menu that opened this, leaving the restore a no-op and the user on the
  // body at the top of the document. The anchor is the control this popover
  // visibly hangs off, and it survives.
  const handleClose = () => {
    if (anchor) {
      pendingRestore.current =
        { target: anchor, held: document.activeElement };
    }
    onClose();
  };

  // Deferred to an effect because the focus trap is still installed until the
  // commit that closes the popover, and it pulls any focus placed outside
  // itself straight back in.
  //
  // No dependency array, for the reason the Stop handler above has none.
  useEffect(() => {
    const pending = pendingRestore.current;
    if (pending === null || anchor !== null) return;
    pendingRestore.current = null;
    // Something else has claimed focus in the meantime -- a panel that a row
    // just opened puts focus in itself -- and a dismissal is no reason to
    // pull the user back out of it.
    const active = document.activeElement;
    if (active !== pending.held && active !== document.body) return;
    // A detached anchor is a node nothing can focus: the header remounts on a
    // route change, so leave focus alone rather than aiming at a dead node.
    if (pending.target.isConnected) pending.target.focus();
  });

  const setPinInput = (id: string) => (element: HTMLInputElement | null) => {
    if (element) pinInputs.current.set(id, element);
    else pinInputs.current.delete(id);
  };

  // Rendered in the order given, and deliberately not re-ranked by what is
  // pinned or running. A row that moves when its own switch is toggled takes
  // the switch with it, so the next click lands on a different tool -- a
  // mis-pin rather than merely a surprise. State is reported in each row's
  // description instead, which says the same thing without moving anything.
  const rows = tools.map((tool) => (
    { tool, isRunning: isRunning(tool), isOpen: isOpen(tool) }));

  if (tools.length === 0) return null;

  // Handed the control this popover hangs off rather than the row that was
  // clicked. A tool that anchors a menu of its own -- demo data is the only
  // one -- would otherwise anchor it to a row inside a popover that is
  // closing, and be left pointing at a detached node once it unmounts.
  const handleOpen = (tool: ToolEntry) => {
    if (!anchor) return;
    handleClose();
    tool.open(anchor);
  };

  // Releasing a tool's frame and whatever it holds. This is the only place
  // anything is discarded: hiding a panel never is, so a user who wants the
  // resources back comes here, where the control can be named for it -- and
  // it still answers to the spec: an un-closable panel is never discarded,
  // whoever asks.
  const stop = (tool: ToolEntry) => {
    if (tool.panelId === undefined || !isStoppable(tool)) return;
    closePanel(tool.panelId);
  };

  // Unpinning something that is running stops it, where stopping is allowed.
  // The taskbar shows pinned and running alike, so leaving a stoppable tool
  // running would keep it on the bar and ignore the only thing the user
  // asked for; an un-closable one stays running because nothing may discard
  // what it holds, and the pill it keeps is the reminder that it does.
  const handlePin = (tool: ToolEntry, next: boolean, running: boolean) => {
    pinned.setPinned(tool.id, next);
    if (!next && running) stop(tool);
  };

  return (
    <Popover
      open={anchor !== null}
      anchorEl={anchor}
      onClose={handleClose}
      // The restore above replaces MUI's rather than racing it: MUI aims at
      // the identity menu row, which is gone by then, so leaving its restore
      // on can only put focus on a dying node ahead of this one.
      disableRestoreFocus
      anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      transformOrigin={{ vertical: 'top', horizontal: 'right' }}
    >
      <Box sx={{ minWidth: 280 }}>
        <Box sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          padding: '8px 16px 4px'
        }}>
          <Typography variant="subtitle2">Tools</Typography>
          {/* A convenience for sighted users. Every switch also names the
              tool it pins, so nothing depends on this being associated. */}
          <Typography variant="caption" sx={{ color: 'text.secondary' }}>
            Pinned
          </Typography>
        </Box>
        <List disablePadding>
          {rows.map(({ tool, isRunning: running, isOpen: open }) => (
            <ListItem
              key={tool.id}
              disablePadding
              secondaryAction={
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  {running && isStoppable(tool) && (
                    <Tooltip describeChild title={`Stop ${tool.name}`}>
                      <IconButton
                        size="small"
                        aria-label={`Stop ${tool.name}`}
                        onClick={() => {
                          pendingFocus.current = tool.id;
                          stop(tool);
                        }}
                      >
                        <StopCircleIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  )}
                  <Switch
                    size="small"
                    edge="end"
                    checked={pinned.isPinned(tool.id)}
                    slotProps={{
                      input: {
                        ref: setPinInput(tool.id),
                        'aria-label': `Pin ${tool.name}`,
                        // The consequence rides the control that carries
                        // it, rather than being learned after the fact --
                        // and only where the consequence is real: an
                        // un-closable tool survives its unpin.
                        title: running && isStoppable(tool) ?
                          `Unpinning will stop ${tool.name}` : undefined,
                        role: 'switch'
                      }
                    }}
                    onChange={(_event, checked) =>
                      handlePin(tool, checked, running)}
                  />
                </Box>
              }
            >
              <ListItemButton
                title={runningDescription(tool.name, running, open)}
                disabled={tool.isDisabled}
                aria-haspopup={tool.popup}
                aria-expanded={
                  tool.popup ? Boolean(tool.isExpanded) : undefined}
                onClick={() => handleOpen(tool)}
              >
                {tool.icon && (
                  <ListItemIcon sx={{ minWidth: 34 }}>
                    {tool.icon}
                  </ListItemIcon>
                )}
                <ListItemText primary={tool.name} />
              </ListItemButton>
            </ListItem>
          ))}
        </List>
      </Box>
    </Popover>
  );
};

export default ToolsPopover;
